import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';
import {
  Users,
  Trophy,
  Target,
  ChevronUp,
  ChevronDown,
  Minus,
  Award
} from 'lucide-react';

interface ComparativeAnalysisProps {
  data: {
    peerComparison: {
      players: Array<{
        name: string;
        distance: number;
        quality: number;
        isCurrentPlayer?: boolean;
      }>;
    };
    benchmarkAnalysis: {
      ageGroup: string;
      skillLevel: string;
      benchmarks: {
        distance: { playerValue: number; benchmark: number; percentile: number };
        consistency: { playerValue: number; benchmark: number; percentile: number };
        accuracy: { playerValue: number; benchmark: number; percentile: number };
        improvement: { playerValue: number; benchmark: number; percentile: number };
      };
    };
    rankingPosition: {
      overall: { position: number; total: number; change: number };
      ageGroup: { position: number; total: number; change: number };
      team: { position: number; total: number; change: number };
    };
  };
  timeRange: string;
}

const COLORS = {
  primary: '#0ea5e9',
  secondary: '#10b981',
  accent: '#f59e0b',
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
  muted: '#6b7280',
  currentPlayer: '#8b5cf6'
};

export function ComparativeAnalysis({ data, timeRange: _timeRange }: ComparativeAnalysisProps) {
  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) return 'text-green-600';
    if (percentile >= 70) return 'text-blue-600';
    if (percentile >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPercentileLabel = (percentile: number) => {
    if (percentile >= 90) return 'Excellent';
    if (percentile >= 70) return 'Good';
    if (percentile >= 50) return 'Average';
    return 'Below Average';
  };

  const getRankingIcon = (change: number) => {
    if (change > 0) return <ChevronUp className="h-4 w-4 text-green-600" />;
    if (change < 0) return <ChevronDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-600" />;
  };

  const getRankingColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="grid gap-6">
      {/* Peer Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Peer Comparison
          </CardTitle>
          <CardDescription>
            Performance comparison with teammates and similar skill level players
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart
              data={data.peerComparison.players}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                type="number" 
                dataKey="distance" 
                name="Distance"
                unit="m"
                stroke="#6b7280"
              />
              <YAxis 
                type="number" 
                dataKey="quality" 
                name="Quality"
                unit="/10"
                stroke="#6b7280"
              />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value, name) => [
                  `${value}${name === 'distance' ? 'm' : '/10'}`, 
                  name === 'distance' ? 'Distance' : 'Quality'
                ]}
                labelFormatter={(_value, payload) =>
                  payload && payload[0] ? payload[0].payload.name : ''
                }
              />
              <Scatter name="Players" dataKey="quality" fill="#8884d8">
                {data.peerComparison.players.map((player, _index) => (
                  <Cell
                    key={`cell-${_index}`} 
                    fill={player.isCurrentPlayer ? COLORS.currentPlayer : COLORS.primary}
                    stroke={player.isCurrentPlayer ? COLORS.currentPlayer : COLORS.primary}
                    strokeWidth={player.isCurrentPlayer ? 3 : 1}
                    r={player.isCurrentPlayer ? 8 : 6}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          
          <div className="mt-4 grid gap-2">
            {data.peerComparison.players.map((player, _index) => (
              <div
                key={player.name}
                className={`flex items-center justify-between p-2 rounded ${
                  player.isCurrentPlayer ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className={`w-3 h-3 rounded-full ${
                      player.isCurrentPlayer ? 'bg-purple-500' : 'bg-blue-500'
                    }`}
                  />
                  <span className={`font-medium ${player.isCurrentPlayer ? 'text-purple-700' : ''}`}>
                    {player.name}
                    {player.isCurrentPlayer && <span className="ml-2 text-xs">(You)</span>}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span>{player.distance.toFixed(1)}m</span>
                  <span>{player.quality.toFixed(1)}/10</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Benchmark Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-green-600" />
            Benchmark Analysis
          </CardTitle>
          <CardDescription>
            Performance against {data.benchmarkAnalysis.ageGroup} age group, {data.benchmarkAnalysis.skillLevel} level
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            {Object.entries(data.benchmarkAnalysis.benchmarks).map(([key, benchmark]) => (
              <div key={key} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</h4>
                  <Badge 
                    variant="outline" 
                    className={getPercentileColor(benchmark.percentile)}
                  >
                    {benchmark.percentile}th percentile
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Your Performance</span>
                    <span className="font-medium">
                      {benchmark.playerValue.toFixed(1)}
                      {key === 'distance' ? 'm' : key === 'improvement' ? '%' : '%'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Group Average</span>
                    <span>
                      {benchmark.benchmark.toFixed(1)}
                      {key === 'distance' ? 'm' : key === 'improvement' ? '%' : '%'}
                    </span>
                  </div>
                  
                  <Progress 
                    value={(benchmark.playerValue / benchmark.benchmark) * 50} 
                    className="h-2" 
                  />
                  
                  <div className={`text-sm font-medium ${getPercentileColor(benchmark.percentile)}`}>
                    {getPercentileLabel(benchmark.percentile)}
                    {benchmark.playerValue > benchmark.benchmark && (
                      <span className="ml-2">
                        (+{((benchmark.playerValue - benchmark.benchmark) / benchmark.benchmark * 100).toFixed(1)}% above average)
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rankings */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-600" />
              Overall Ranking
            </CardTitle>
            <CardDescription>
              Academy-wide position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold">
                #{data.rankingPosition.overall.position}
              </div>
              <div className="text-sm text-muted-foreground">
                out of {data.rankingPosition.overall.total} players
              </div>
              <div className={`flex items-center justify-center gap-1 text-sm ${getRankingColor(data.rankingPosition.overall.change)}`}>
                {getRankingIcon(data.rankingPosition.overall.change)}
                <span>
                  {data.rankingPosition.overall.change === 0 ? 'No change' :
                   data.rankingPosition.overall.change > 0 ? `+${data.rankingPosition.overall.change} positions` :
                   `${data.rankingPosition.overall.change} positions`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Age Group Ranking
            </CardTitle>
            <CardDescription>
              {data.benchmarkAnalysis.ageGroup} years position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold">
                #{data.rankingPosition.ageGroup.position}
              </div>
              <div className="text-sm text-muted-foreground">
                out of {data.rankingPosition.ageGroup.total} players
              </div>
              <div className={`flex items-center justify-center gap-1 text-sm ${getRankingColor(data.rankingPosition.ageGroup.change)}`}>
                {getRankingIcon(data.rankingPosition.ageGroup.change)}
                <span>
                  {data.rankingPosition.ageGroup.change === 0 ? 'No change' :
                   data.rankingPosition.ageGroup.change > 0 ? `+${data.rankingPosition.ageGroup.change} positions` :
                   `${data.rankingPosition.ageGroup.change} positions`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-600" />
              Team Ranking
            </CardTitle>
            <CardDescription>
              Current team position
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold">
                #{data.rankingPosition.team.position}
              </div>
              <div className="text-sm text-muted-foreground">
                out of {data.rankingPosition.team.total} players
              </div>
              <div className={`flex items-center justify-center gap-1 text-sm ${getRankingColor(data.rankingPosition.team.change)}`}>
                {getRankingIcon(data.rankingPosition.team.change)}
                <span>
                  {data.rankingPosition.team.change === 0 ? 'No change' :
                   data.rankingPosition.team.change > 0 ? `+${data.rankingPosition.team.change} positions` :
                   `${data.rankingPosition.team.change} positions`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}